import {
    booleanAttribute,
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    EventEmitter,
    inject,
    input,
    Output,
    ViewChild
} from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { IdGeneratorPipe } from '../../pipes/id-generator.pipe';
import { IconSanitizerService } from '../../services/icon-sanitizer.service';

@Component({
    selector: 'lib-search-input',
    templateUrl: './search-input.component.html',
    styleUrls: ['./search-input.component.scss'],
    imports: [IdGeneratorPipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchInputComponent {
    @ViewChild('inputElement')
    inputElement?: ElementRef<HTMLInputElement>;

    private readonly iconSanitizer = inject(IconSanitizerService);
    private readonly destroyRef = inject(DestroyRef);

    placeholderText = input<string>();

    text = input('', { transform: (value: string | undefined) => value ?? '' });

    searchIcon = input(true, { transform: (value: boolean | undefined) => value ?? true });

    removeBorder = input(false, { transform: booleanAttribute });

    clearValueButton = input(false, { transform: booleanAttribute });

    disabled = input(false, { transform: booleanAttribute });

    error = input(false, { transform: booleanAttribute });

    searchInputId = input<string>();

    useTimeout = input(true, { transform: booleanAttribute });

    @Output()
    textChange: EventEmitter<string> = new EventEmitter<string>();

    @Output()
    blurEvent: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();

    @Output()
    focusChange = new EventEmitter<FocusEvent>();

    readonly closeIcon: SafeHtml = this.iconSanitizer.getIcon('close');

    readonly iconSearch: SafeHtml = this.iconSanitizer.getIcon('search');

    private timeout?: ReturnType<typeof setTimeout>;

    constructor() {
        this.destroyRef.onDestroy(() => this.clearPendingTimeout());
    }

    onInput(event: Event): void {
        this.clearPendingTimeout();

        if (this.useTimeout()) {
            this.timeout = setTimeout(() => this.emitChanges(event), 500);
            return;
        }

        this.emitChanges(event);
    }

    emitChanges(event: Event): void {
        this.textChange.emit((event.target as HTMLInputElement).value);
    }

    emitBlurEvent(event: FocusEvent): void {
        this.blurEvent.emit(event);
    }

    clearValue(): void {
        if (this.inputElement) {
            this.inputElement.nativeElement.value = '';
        }

        this.textChange.emit('');
    }

    onFocus(event: FocusEvent): void {
        this.focusChange.emit(event);
    }

    private clearPendingTimeout(): void {
        if (this.timeout) {
            clearTimeout(this.timeout);
            this.timeout = undefined;
        }
    }
}
